import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, hasPermission } from '@blimo/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, ErrorState, Skeleton } from '@blimo/ui';
import { AuditDiff } from './audit.jsx';

/**
 * Compact „Verlauf“ of one record for detail pages (last 10 audit entries, expandable diff).
 * Renders nothing without `audit-log.view` (the API checks again).
 * @param {{ subjectType: string, subjectId: string|number, title?: string, limit?: number }} props
 */
export function HistoryPanel({ subjectType, subjectId, title = 'Verlauf', limit = 10 }) {
  const { data: me } = useMe();
  if (!hasPermission(me, 'audit-log.view')) return null;
  return <HistoryPanelContent subjectType={subjectType} subjectId={subjectId} title={title} limit={limit} />;
}

function HistoryPanelContent({ subjectType, subjectId, title, limit }) {
  const api = useApi();
  const query = useQuery(adminQueries.auditLog(api, { per_page: limit, filter: { subject_type: subjectType, subject_id: subjectId } }));
  const entries = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle as="h2" className="flex items-center gap-2">
          <History className="size-4 text-muted-foreground" aria-hidden="true" /> {title}
        </CardTitle>
        <Button asChild size="sm" variant="ghost">
          <Link to="/administration/aenderungsprotokoll" search={{ subject_type: subjectType, subject_id: String(subjectId) }}>
            Alle Einträge{total > limit ? ` (${total})` : ''}
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {query.error ? (
          <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
        ) : query.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine protokollierten Änderungen.</p>
        ) : (
          <ol className="flex flex-col divide-y" aria-label={title}>
            {entries.map((entry) => (
              <HistoryEntry key={entry.id} entry={entry} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const api = useApi();
  const detail = useQuery({ ...adminQueries.auditLogEntry(api, entry.id), enabled: open });
  const canExpand = entry.changed_attributes?.length > 0;
  return (
    <li className="py-2 text-sm">
      <div className="flex items-start gap-2">
        {canExpand ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-expanded={open}
            aria-label={`Änderungen vom ${formatDateTime(entry.created_at)} anzeigen`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
          </Button>
        ) : (
          <span className="size-7 shrink-0" aria-hidden="true" />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <span>
            <span className="font-medium">{entry.event_label}</span>
            {canExpand && <span className="text-muted-foreground"> · {entry.changed_attributes.join(', ')}</span>}
          </span>
          <span className="text-xs text-muted-foreground">
            <span className="numeric">{formatDateTime(entry.created_at)}</span> · {entry.causer?.name ?? 'System'}
          </span>
        </div>
      </div>
      {open && (
        <div className="mt-2 pl-9">
          {detail.data ? (
            <AuditDiff changes={detail.data.changes} />
          ) : detail.error ? (
            <ErrorState compact error={detail.error} />
          ) : (
            <Skeleton className="h-12 w-full" />
          )}
        </div>
      )}
    </li>
  );
}
